import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { auth, db } from './firebase';

export const registerUser = async (email, password, pharmacyName) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    await updateProfile(user, {
      displayName: pharmacyName
    });
    
    await setDoc(doc(db, 'users', user.uid), {
      email: user.email,
      pharmacyName: pharmacyName,
      createdAt: new Date().toISOString(),
      plan: 'free',
      catalogoHabilitado: true // Indicador de que puede acceder al catálogo
    });
    
    return user;
  } catch (error) {
    throw error;
  }
};

export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Actualizar último acceso
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        // Si el documento existe, actualizarlo
        await updateDoc(userDocRef, {
          lastLogin: new Date().toISOString()
        });
      } else {
        // Si no existe (usuario antiguo), crear el documento
        await setDoc(userDocRef, {
          email: user.email,
          pharmacyName: user.displayName || '',
          createdAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          plan: 'free'
        });
      }
    } catch (updateError) {
      console.error('Error updating last login:', updateError);
      // No lanzar el error para no interrumpir el login
    }
    
    return user;
  } catch (error) {
    throw error;
  }
};

const ensureUserDoc = async (user) => {
  const userDocRef = doc(db, 'users', user.uid);
  const userDoc = await getDoc(userDocRef);
  const now = new Date().toISOString();

  if (userDoc.exists()) {
    await updateDoc(userDocRef, { lastLogin: now });
    return { isNew: false };
  }

  await setDoc(userDocRef, {
    email: user.email,
    pharmacyName: user.displayName || '',
    photoURL: user.photoURL || '',
    createdAt: now,
    lastLogin: now,
    plan: 'free',
    catalogoHabilitado: true,
    provider: 'google.com'
  });
  return { isNew: true };
};

export const signInWithGoogle = async () => {
  try {
    let user;

    if (Capacitor.isNativePlatform()) {
      const result = await FirebaseAuthentication.signInWithGoogle({
        useCredentialManager: false
      });
      const idToken = result.credential?.idToken;
      if (!idToken) {
        throw new Error('No se recibió el token de Google');
      }
      const credential = GoogleAuthProvider.credential(idToken);
      const userCredential = await signInWithCredential(auth, credential);
      user = userCredential.user;
    } else {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const userCredential = await signInWithPopup(auth, provider);
      user = userCredential.user;
    }

    await ensureUserDoc(user);
    return user;
  } catch (error) {
    console.error('Google sign-in error:', error);
    throw error;
  }
};

export const logoutUser = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      try {
        await FirebaseAuthentication.signOut();
      } catch (e) {
        console.warn('Native signOut warning:', e);
      }
    }
    await signOut(auth);
  } catch (error) {
    throw error;
  }
};

export const getCurrentUser = () => {
  return auth.currentUser;
};

export const getUserData = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

export const subscribeToAuthChanges = (callback) => {
  return onAuthStateChanged(auth, callback);
};